import { isNil } from 'lodash';
import React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import {
  isProFormaBlockOrMetricBenchmark,
  isReportState,
  isReturnsBasedBlockState,
  type StudioRequestSubject,
  subjectAsOfDate,
} from 'venn-state';
import {
  FootnoteSymbols,
  getComparisonLabelForBlockLegend,
  getSubjectFromRequestSubject,
  hasFeesExcluded,
  isHistoricalSubject,
  Numbers,
} from 'venn-utils';
import { formatDateByFrequency } from '../../../../manage-data';
import { GetColor } from 'venn-ui-kit';
import { useBlockSubjectSuperscripts } from '../../core/block-subject-footnotes-and-superscripts';
import { useGetRequestSubjectFee } from '../../../utils/hooks/use-advisory-fee';

interface NameProps {
  id: string;
  subject: StudioRequestSubject;
  isBenchmark: boolean;
}

export const Name = ({ id, subject, isBenchmark }: NameProps) => {
  const getRequestSubjectFee = useGetRequestSubjectFee();
  const name = getComparisonLabelForBlockLegend(subject);
  const isReturnsBasedBlock = useRecoilValue(isReturnsBasedBlockState(id));
  const feesExcluded = hasFeesExcluded(subject);
  const fee = getRequestSubjectFee({ subject, isBenchmark });
  const isStudio = !useRecoilValue(isReportState);
  const historicalAsOfDate = useRecoilValue(subjectAsOfDate([id, getSubjectFromRequestSubject(subject)]));
  const useProFormaForHistoricalBenchmarksBlock = useRecoilValue(isProFormaBlockOrMetricBenchmark(id));
  const hasHistoricalAsOfDate =
    isHistoricalSubject(subject) &&
    !isNil(historicalAsOfDate) &&
    (!isBenchmark || (isBenchmark && useProFormaForHistoricalBenchmarksBlock));
  const showHistoricalAsOfDate = isStudio && hasHistoricalAsOfDate;

  const superscripts = useBlockSubjectSuperscripts({ subject, isBenchmark });

  return (
    <Wrapper>
      <Container>{[name, <sup>{superscripts}</sup>]}</Container>
      {showHistoricalAsOfDate && (
        <HistoricalAsOfDate> (as of {formatDateByFrequency(historicalAsOfDate, 'DAILY')})</HistoricalAsOfDate>
      )}
      {isReturnsBasedBlock && fee !== undefined && (
        <Fee>
          ({Numbers.safeFormatPercentage(fee)} Fee){feesExcluded && FootnoteSymbols.excludedInvestments}
        </Fee>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-width: 0;
  display: flex;
  align-content: center;
  gap: 4px;
`;

const Container = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Fee = styled.span`
  font-style: italic;
  font-weight: normal;
`;

const HistoricalAsOfDate = styled.span`
  font-weight: normal;
  color: ${GetColor.GreyScale.Grey80};
`;
